import { Injectable } from "@angular/core";
import { BehaviorSubject, debounceTime } from "rxjs";
import { distinctUntilChanged, map, withLatestFrom } from "rxjs/operators";


@Injectable({
    providedIn: 'root',
})
export class VideoPlayerConfigService {
    private readonly pauseEmitVideoEvents = new BehaviorSubject<boolean>(false);
    readonly pauseEmitVideoEvents$ = this.pauseEmitVideoEvents.asObservable();

    private readonly emitVideoEvents = new BehaviorSubject<boolean>(true);
    readonly emitVideoChanges$ = this.emitVideoEvents.asObservable().pipe(
        withLatestFrom(this.pauseEmitVideoEvents$),
        map(([emitChanges, pauseEmissions]) => emitChanges),
        // map(([emitChanges, pauseEmissions]) => emitChanges && !pauseEmissions),
    );

    private readonly volumeCacheStorage: Storage = window.localStorage;
    private readonly volumeCacheKey: string = 'config.video.volume';

    private readonly volumeSubject = new BehaviorSubject<undefined|number>(this.getVolumeFromCache());
    readonly volume$ = this.volumeSubject.asObservable().pipe(
        debounceTime(100),
        distinctUntilChanged(),
    );

    setEmitVideoChanges(emit: boolean) {
        this.emitVideoEvents.next(emit);
    }

    setPauseEmitVideoChanges(pause: boolean) {
        this.pauseEmitVideoEvents.next(pause);
    }

    mute = () => this.setVolume(0);

    setVolume(volume: number) {
        if (this.getVolume() === volume) {
            return;
        }

        this.saveVolumeToCache(volume);
        this.volumeSubject.next(volume);
    }

    getVolume = () => this.volumeSubject.value;

    /**
     * @returns Cached volume from localStorage or `undefined` if it doesn't exist or is not a valid number.
     */
    private getVolumeFromCache(): undefined | number {
        const volumeStr = this.volumeCacheStorage.getItem(this.volumeCacheKey);
        const volume = volumeStr ? Number.parseFloat(volumeStr) : undefined;
        if (typeof volume === 'number' && Number.isFinite(volume) && volume >= 0 && volume <= 1) {
            return volume;
        }

        return undefined;
    }

    private saveVolumeToCache(volume: number) {
        this.volumeCacheStorage.setItem(this.volumeCacheKey, '' + volume);
    }
}
